import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Row, Col, FormText } from 'reactstrap';
import { isNumber, Translate, translate, ValidatedField, ValidatedForm } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { mapIdList } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { IPartenaire } from 'app/shared/model/partenaire.model';
import { getEntities as getPartenaires } from 'app/entities/partenaire/partenaire.reducer';
import { IDevis } from 'app/shared/model/devis.model';
import { getEntities as getDevis } from 'app/entities/devis/devis.reducer';
import { IConteneur } from 'app/shared/model/conteneur.model';
import { getEntity, updateEntity, createEntity, reset } from './conteneur.reducer';

export const ConteneurUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const partenaires = useAppSelector(state => state.partenaire.entities);
  const devis = useAppSelector(state => state.devis.entities);
  const conteneurEntity = useAppSelector(state => state.conteneur.entity);
  const loading = useAppSelector(state => state.conteneur.loading);
  const updating = useAppSelector(state => state.conteneur.updating);
  const updateSuccess = useAppSelector(state => state.conteneur.updateSuccess);

  const handleClose = () => {
    navigate('/conteneur' + location.search);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getPartenaires({}));
    dispatch(getDevis({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  // eslint-disable-next-line complexity
  const saveEntity = values => {
    if (values.id !== undefined && typeof values.id !== 'number') {
      values.id = Number(values.id);
    }
    if (values.prixDeTransit !== undefined && typeof values.prixDeTransit !== 'number') {
      values.prixDeTransit = Number(values.prixDeTransit);
    }
    if (values.poids !== undefined && typeof values.poids !== 'number') {
      values.poids = Number(values.poids);
    }
    values.datePersistence = convertDateTimeToServer(values.datePersistence);
    values.dateUpdate = convertDateTimeToServer(values.dateUpdate);

    const entity = {
      ...conteneurEntity,
      ...values,
      partenaire: partenaires.find(it => it.id.toString() === values.partenaire?.toString()),
      devis: devis.find(it => it.id.toString() === values.devis?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          datePersistence: displayDefaultDateTime(),
          dateUpdate: displayDefaultDateTime(),
        }
      : {
          ...conteneurEntity,
          datePersistence: convertDateTimeFromServer(conteneurEntity.datePersistence),
          dateUpdate: convertDateTimeFromServer(conteneurEntity.dateUpdate),
          partenaire: conteneurEntity?.partenaire?.id,
          devis: conteneurEntity?.devis?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="alexisExportApp.conteneur.home.createOrEditLabel" data-cy="ConteneurCreateUpdateHeading">
            <Translate contentKey="alexisExportApp.conteneur.home.createOrEditLabel">Create or edit a Conteneur</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="conteneur-id"
                  label={translate('global.field.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('alexisExportApp.conteneur.prixDeTransit')}
                id="conteneur-prixDeTransit"
                name="prixDeTransit"
                data-cy="prixDeTransit"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('alexisExportApp.conteneur.destination')}
                id="conteneur-destination"
                name="destination"
                data-cy="destination"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('alexisExportApp.conteneur.poids')}
                id="conteneur-poids"
                name="poids"
                data-cy="poids"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('alexisExportApp.conteneur.datePersistence')}
                id="conteneur-datePersistence"
                name="datePersistence"
                data-cy="datePersistence"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                }}
              />
              <ValidatedField
                label={translate('alexisExportApp.conteneur.dateUpdate')}
                id="conteneur-dateUpdate"
                name="dateUpdate"
                data-cy="dateUpdate"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                id="conteneur-partenaire"
                name="partenaire"
                data-cy="partenaire"
                label={translate('alexisExportApp.conteneur.partenaire')}
                type="select"
              >
                <option value="" key="0" />
                {partenaires
                  ? partenaires.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="conteneur-devis"
                name="devis"
                data-cy="devis"
                label={translate('alexisExportApp.conteneur.devis')}
                type="select"
              >
                <option value="" key="0" />
                {devis
                  ? devis.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/conteneur" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ConteneurUpdate;
